import React, { useCallback, useEffect, useState } from 'react';

import { Group, Modal, Title, Text, useMantineTheme } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useTaxRates from 'hooks/taxRate/useTaxRates';
import { CellProps } from 'react-table';
import { TaxRate } from 'types';
import { threeDecimalFormatter } from 'utils/formatters';

import DeleteTaxRateModal from './DeleteTaxRateModal';
import DrawerTaxRateForm from './DrawerTaxRateForm';

const TaxRatesTableContainer = ({
  searchTerm,
  permission = true,
}: {
  searchTerm: string;
  permission?: boolean;
}): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useTaxRates({
      search: searchTerm,
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [tableData, setTableData] = useState<TaxRate[]>([]);
  const [selectedTaxRate, setSelectedTaxRate] = useState<TaxRate>();
  const [isDeleteModalOpen, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);
  const [isEditFormOpen, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleCloseEditForm = (): void => {
    setSelectedTaxRate(undefined);
    closeEditForm();
  };

  const handleEdit = (taxRate: TaxRate): void => {
    setSelectedTaxRate(taxRate);
    openEditForm();
  };

  const handleDelete = (taxRate: TaxRate): void => {
    setSelectedTaxRate(taxRate);
    openDeleteModal();
  };

  const columns = [
    {
      Header: 'Name',
      accessor: 'name',
      Cell: ({ row }: CellProps<TaxRate>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Percentage',
      accessor: 'rate' as const,
      Cell: ({ value }: { value: number }) => `${threeDecimalFormatter(value * 100)}%`,
    },
    {
      id: 'action',
      disableSortBy: true,
      Cell: ({ row }: CellProps<TaxRate>) => (
        <Group position='right' px='xs'>
          <Menu
            disabled={!permission}
            position='bottom-end'
            items={[
              {
                title: 'Edit',
                icon: <IconEdit size={theme.fontSizes.sm} />,
                onClick: () => handleEdit(row.original),
              },
              {
                title: 'Delete',
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
                onClick: () => handleDelete(row.original),
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'percentage']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />
      <DrawerTaxRateForm
        taxRateId={selectedTaxRate?.taxRateId}
        opened={isEditFormOpen}
        closeHandler={handleCloseEditForm}
        formId='edit-tax-rate-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Tax Rate</Title>}
        type='edit'
        permission={permission}
      />
      {selectedTaxRate && (
        <Modal
          opened={isDeleteModalOpen}
          onClose={closeDeleteModal}
          title={<Title order={4}>Delete Tax rate</Title>}
          trapFocus
          centered
          size='auto'
        >
          <DeleteTaxRateModal taxRate={selectedTaxRate} handleClose={closeDeleteModal} />
        </Modal>
      )}
    </>
  );
};

export default TaxRatesTableContainer;
